import type { PrimitiveCameraFocus } from '@/adapters/primitive/_contract/camera';
import type { PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

export function selectPrimitiveCameraWheels(
    step: PrimitiveStepState, wheels: readonly PrimitiveWheelState[], focus: PrimitiveCameraFocus,
): PrimitiveWheelState[] {
    const selected = wheels.filter((wheel) => focus.roles.includes(wheel.role)
        || wheel.receiverLocation?.receiverRole === focus.targetRole);
    if (focus.power === undefined || !focus.stationKey.startsWith('working:')) return selected;
    const powersByRole = new Map<PrimitiveWheelRole, Set<number>>();
    const wholeWheelRoles = new Set<PrimitiveWheelRole>();
    for (const role of focus.roles) powersByRole.set(role, new Set([focus.power]));
    for (const frame of step.keyframes) for (const { event } of frame.events) {
        const movingEntity = 'contributionTraveller' in event ? event.contributionTraveller
            : 'operandWheel' in event ? event.operandWheel : undefined;
        if (movingEntity) {
            if ('power' in movingEntity && typeof movingEntity.power === 'number') {
                const powers = powersByRole.get(movingEntity.wheelRole) ?? new Set<number>();
                powers.add(movingEntity.power);
                powersByRole.set(movingEntity.wheelRole, powers);
            } else wholeWheelRoles.add(movingEntity.wheelRole);
        }
        if ('receiverInteraction' in event) {
            const receiver = event.receiverInteraction.receiver;
            const powers = powersByRole.get(receiver.wheelRole) ?? new Set<number>();
            powers.add(receiver.power);
            powersByRole.set(receiver.wheelRole, powers);
        }
    }
    return selected.map((wheel) => wheel.variant !== undefined || wholeWheelRoles.has(wheel.role) ? wheel : {
        ...wheel, powers: wheel.powers.filter((power) => powersByRole.get(wheel.role)?.has(power)),
    }).filter((wheel) => wheel.powers.length > 0);
}

export function derivePrimitiveCameraFocus(
    step: PrimitiveStepState, previousWheels: readonly PrimitiveWheelState[],
): PrimitiveCameraFocus {
    if (step.cameraFocus) return step.cameraFocus;
    const wheels = step.keyframes.flatMap((frame) => [...frame.wheels]);
    const roles = [...new Set(wheels.filter((wheel) => wheel.variant === undefined).map((wheel) => wheel.role))];
    if (step.keyframes.some((frame) => frame.booleanRod !== undefined)) {
        return { stationKey: 'boolean-graph', viewKey: 'boolean-junction', roles };
    }
    if (roles.includes('less-than-source')) {
        return { stationKey: 'comparison', viewKey: 'comparison', roles };
    }
    let receiver: PrimitiveWheelRole | undefined;
    let power: number | undefined;
    const participants = new Set<PrimitiveWheelRole>();
    for (const frame of step.keyframes) {
        for (const envelope of frame.events) {
            const event = envelope.event;
            if ('contributionTraveller' in event) participants.add(event.contributionTraveller.wheelRole);
            else if ('operandWheel' in event) participants.add(event.operandWheel.wheelRole);
            if ('receiverInteraction' in event) {
                receiver = event.receiverInteraction.receiver.wheelRole;
                power = event.receiverInteraction.receiver.power;
            } else if ('location' in event) receiver = event.location.receiverRole;
            else if ('receiverRole' in event) receiver = event.receiverRole;
        }
        for (const wheel of frame.wheels) {
            if (wheel.receiverLocation && wheel.receiverLocation.locationKind !== 'source') {
                receiver ??= wheel.receiverLocation.receiverRole;
                power ??= wheel.powers[0];
                participants.add(wheel.role);
            }
        }
    }
    if (!receiver) {
        const previousByRole = new Map(previousWheels.filter((wheel) => wheel.variant === undefined)
            .map((wheel) => [wheel.role, wheel]));
        const changed = wheels.filter((wheel) => wheel.variant === undefined && previousByRole.has(wheel.role)
            && JSON.stringify(wheel) !== JSON.stringify(previousByRole.get(wheel.role)));
        receiver = changed.at(-1)?.role;
    }
    if (!receiver) return { stationKey: 'overview', viewKey: 'overview', roles };
    participants.add(receiver);
    return {
        stationKey: `working:${receiver}:${power ?? 'wheel'}`,
        viewKey: `working:${receiver}:${power ?? 'wheel'}`,
        roles: [...participants],
        targetRole: receiver,
        ...(power === undefined ? {} : { power }),
    };
}
